/**
 * wd-2.3.1 — localStorage-backed draft + offline retry queue for the
 * prospect-facing intake portal.
 *
 * The intake's canonical state lives on the server (`TenantIntake.payload`
 * is merged on each successful `POST /:token/step`). This client-side
 * store complements the server:
 *
 *   - **draft.payload** holds partial per-step edits that haven't yet
 *     passed the server's Zod schema. Keyed by token so two browsers
 *     can each hold independent drafts for the same invite without
 *     stomping each other (field-level last-write-wins is a server
 *     problem, not a client one).
 *   - **draft.lastStep** is the step the prospect is currently viewing,
 *     which may be ahead of the server's committed `lastStep` (user
 *     clicked Back) but never below it (server is the floor).
 *   - **retryQueue** holds step submissions that failed for network
 *     reasons so an `online` event can replay them. Schema-rejected
 *     submissions never hit the queue — they're user-fixable and the
 *     UI surfaces the typed issue list.
 *
 * Keyed per-token so multiple prospects on a shared kiosk can't cross-
 * contaminate drafts.
 */

export const INTAKE_STEP_IDS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
] as const;

export type IntakeStepId = (typeof INTAKE_STEP_IDS)[number];
export const INTAKE_STEP_COUNT = INTAKE_STEP_IDS.length;

export const INTAKE_STEP_LABELS: Record<IntakeStepId, string> = {
  '1': 'Business basics',
  '2': 'Brand kit',
  '3': 'Stripe Connect',
  '4': 'Payments & accounting',
  '5': 'Staff roster',
  '6': 'Domain preference',
  '7': 'Service catalog',
  '8': 'Module activation',
  '9': 'Data migration',
  '10': 'Review & submit',
};

const DRAFT_SCHEMA_VERSION = 1;
const DRAFT_STORAGE_PREFIX = 'ferro.intake.draft.v1.';
const RETRY_QUEUE_PREFIX = 'ferro.intake.retry.v1.';

export type IntakeDraft = {
  version: typeof DRAFT_SCHEMA_VERSION;
  token: string;
  /** The step the prospect is currently editing (1..10). */
  currentStep: IntakeStepId;
  /** Partial per-step edits keyed by step id (`step1`, `step2`, …). */
  payload: Record<string, unknown>;
  updatedAt: string;
};

export type IntakeRetryEntry = {
  stepId: IntakeStepId;
  payload: unknown;
  queuedAt: string;
};

export type IntakeRetryQueue = {
  version: typeof DRAFT_SCHEMA_VERSION;
  token: string;
  entries: IntakeRetryEntry[];
};

function isIntakeStepId(value: unknown): value is IntakeStepId {
  return typeof value === 'string'
    && (INTAKE_STEP_IDS as readonly string[]).includes(value);
}

function isPayloadRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function draftStorageKey(token: string): string {
  return `${DRAFT_STORAGE_PREFIX}${token}`;
}

export function retryQueueStorageKey(token: string): string {
  return `${RETRY_QUEUE_PREFIX}${token}`;
}

export function stepIdToNumber(stepId: IntakeStepId): number {
  return Number.parseInt(stepId, 10);
}

export function numberToStepId(n: number): IntakeStepId | null {
  const clamped = Math.max(1, Math.min(INTAKE_STEP_COUNT, Math.round(n)));
  const s = String(clamped);
  return isIntakeStepId(s) ? s : null;
}

/**
 * The step the UI should render. `lastStep` on the server is the
 * highest-completed step (or 1 for a fresh draft); the prospect may
 * navigate back without losing that cursor. The client "currentStep"
 * is always ≥ 1 and ≤ 10; if the server's `lastStep` is ahead of a
 * stale client draft, the server wins.
 */
export function reconcileCurrentStep(
  serverLastStep: number,
  clientDraft: IntakeDraft | null,
): IntakeStepId {
  const serverStep = numberToStepId(serverLastStep) ?? '1';
  if (!clientDraft) return serverStep;
  const serverN = stepIdToNumber(serverStep);
  const clientN = stepIdToNumber(clientDraft.currentStep);
  // Client may be ahead (user clicked Next before their submission
  // persisted) or behind (user clicked Back). Prefer whichever is
  // higher so the prospect never *loses* ground on reload.
  return clientN >= serverN ? clientDraft.currentStep : serverStep;
}

export function nextIntakeStep(step: IntakeStepId): IntakeStepId | null {
  const n = stepIdToNumber(step);
  return n === INTAKE_STEP_COUNT ? null : (String(n + 1) as IntakeStepId);
}

export function prevIntakeStep(step: IntakeStepId): IntakeStepId | null {
  const n = stepIdToNumber(step);
  return n === 1 ? null : (String(n - 1) as IntakeStepId);
}

export function parseStoredDraft(raw: string | null): IntakeDraft | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isPayloadRecord(parsed)) return null;
  if (parsed.version !== DRAFT_SCHEMA_VERSION) return null;
  if (typeof parsed.token !== 'string' || parsed.token === '') return null;
  if (!isIntakeStepId(parsed.currentStep)) return null;
  if (!isPayloadRecord(parsed.payload)) return null;
  if (typeof parsed.updatedAt !== 'string') return null;
  return {
    version: DRAFT_SCHEMA_VERSION,
    token: parsed.token,
    currentStep: parsed.currentStep,
    payload: parsed.payload,
    updatedAt: parsed.updatedAt,
  };
}

export function readDraft(token: string): IntakeDraft | null {
  if (!token) return null;
  const stored = parseStoredDraft(
    window.localStorage.getItem(draftStorageKey(token)),
  );
  if (!stored) return null;
  return stored.token === token ? stored : null;
}

export function writeDraft(
  draft: Omit<IntakeDraft, 'version' | 'updatedAt'>,
  now: Date = new Date(),
): IntakeDraft {
  const full: IntakeDraft = {
    version: DRAFT_SCHEMA_VERSION,
    token: draft.token,
    currentStep: draft.currentStep,
    payload: draft.payload,
    updatedAt: now.toISOString(),
  };
  window.localStorage.setItem(
    draftStorageKey(draft.token),
    JSON.stringify(full),
  );
  return full;
}

export function clearDraft(token: string): void {
  if (!token) return;
  window.localStorage.removeItem(draftStorageKey(token));
}

export function parseStoredRetryQueue(raw: string | null): IntakeRetryQueue | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isPayloadRecord(parsed)) return null;
  if (parsed.version !== DRAFT_SCHEMA_VERSION) return null;
  if (typeof parsed.token !== 'string' || parsed.token === '') return null;
  if (!Array.isArray(parsed.entries)) return null;
  const entries: IntakeRetryEntry[] = [];
  for (const raw of parsed.entries) {
    if (!isPayloadRecord(raw)) continue;
    if (!isIntakeStepId(raw.stepId)) continue;
    if (typeof raw.queuedAt !== 'string') continue;
    entries.push({
      stepId: raw.stepId,
      payload: raw.payload,
      queuedAt: raw.queuedAt,
    });
  }
  return { version: DRAFT_SCHEMA_VERSION, token: parsed.token, entries };
}

export function readRetryQueue(token: string): IntakeRetryQueue {
  if (!token) return { version: DRAFT_SCHEMA_VERSION, token: '', entries: [] };
  const stored = parseStoredRetryQueue(
    window.localStorage.getItem(retryQueueStorageKey(token)),
  );
  if (!stored || stored.token !== token) {
    return { version: DRAFT_SCHEMA_VERSION, token, entries: [] };
  }
  return stored;
}

export function writeRetryQueue(queue: IntakeRetryQueue): void {
  if (!queue.token) return;
  window.localStorage.setItem(
    retryQueueStorageKey(queue.token),
    JSON.stringify(queue),
  );
}

export function enqueueRetry(
  token: string,
  entry: Omit<IntakeRetryEntry, 'queuedAt'>,
  now: Date = new Date(),
): IntakeRetryQueue {
  const current = readRetryQueue(token);
  // Dedup by stepId — the latest pending submission for a given step
  // wins. If the user typed a fix between failures, we don't want to
  // replay the old payload then overwrite it with the new one.
  const next: IntakeRetryQueue = {
    version: DRAFT_SCHEMA_VERSION,
    token,
    entries: [
      ...current.entries.filter((e) => e.stepId !== entry.stepId),
      { ...entry, queuedAt: now.toISOString() },
    ],
  };
  writeRetryQueue(next);
  return next;
}

export function dropRetry(token: string, stepId: IntakeStepId): IntakeRetryQueue {
  const current = readRetryQueue(token);
  const next: IntakeRetryQueue = {
    ...current,
    entries: current.entries.filter((e) => e.stepId !== stepId),
  };
  writeRetryQueue(next);
  return next;
}

export function clearRetryQueue(token: string): void {
  if (!token) return;
  window.localStorage.removeItem(retryQueueStorageKey(token));
}
